import axios, { AxiosResponse } from 'axios'
import config from '../../config'
import {
  IAuthContext,
  IGetUserImage,
  IResultQueryReservationJoinOnUserAndChargeAndVehicle,
} from './bookingCheckin.interface'

const { restfulService, uploadService } = config

// Response interface for the API
export interface IApiResponse {
  result?: IResultQueryReservationJoinOnUserAndChargeAndVehicle
  payload?: Record<string, unknown>
  message?: string
  error?: string
  statusCode?: number
}

export interface IApiRequestGetOmiselink {
  chargemethod: string
  genlink: boolean
  genlink_promptpay: boolean
  channel: string
}

export interface IOmiselinkResponse {
  utf8: boolean
  requesttime: string
  result: string
  path: string
  userid: number
  chargeresult: boolean
  charge: {
    duedate: string | null
    mobile: string
    chargeno: number
    receiptno: string
    userid: number
    description: string
    reservationno: number
    chargetime: string
    reversetime: string
    createlink_time: string
    charged_time: string | null
    chargeevent: string
    chargemethod: string
    chargeid: string
    chargeaccount: string
    chargestate: string
    chargestatus: string
    chargetotal: number
    original_chargetotal: number
    original_charge_currency: string
    initial_chargetotal: number
    initial_chargetotal_currency: string
    exchange_rate_amount: number
    exchange_rate_from_currency: string
    exchange_rate_to_currency: string
    charge_multiple_currency: boolean
    otp_url: string
    paymentno: number
    charge_hour: number
    charge_distance: number
    charge_deposit: number
    charge_paymentfee: number
    charge_channelfee: number
    charge_markup: number
    charge_latefee: number
    charge_fine: number
    charge_deductable: number
    charge_other: number
    charge_protection: number
    charge_vat: number
    link_url: string
    link_id: string
    link_expire_at: string | null
    transferslip_url: string
    callback_url: string
    holding: boolean
    transfer_from_bank: string
    transfer_to_bank: string
    transfer_number: string
    transfer_time: string | null
    payment_channel: string
    service_name: string | null
    service_ref_id: string | null
    dueindex: number
    totaldue: number
  }
  responsetime: string
}
// Simple Process Omise Token interfaces
export interface ProcessOmiseTokenRequest {
  omise_token: string
}

export interface ProcessOmiseTokenResponse {
  result: boolean
  message?: string
}

/**
 * Fetch reservation information using token authorization
 * @param token - Authorization token
 * @returns Promise<IApiResponse>
 */
export const getReservationInfo = async (token: string): Promise<IApiResponse> => {
  try {
    const response: AxiosResponse<IApiResponse> = await axios.get(
      `${restfulService.URL}/customer-api/reservation-info`,
      {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      },
    )

    return response.data
  } catch (error: unknown) {
    // Handle axios error response
    if (axios.isAxiosError(error) && error.response) {
      return {
        message:
          error.response.data?.message ||
          `HTTP ${error.response.status}: ${error.response.statusText}`,
      }
    }

    // Handle network or other errors
    return {
      message: error instanceof Error ? error.message : 'Network error occurred',
    }
  }
}

export async function postUploadUserImage(
  auth: IAuthContext,
  params: IGetUserImage,
  body: FormData,
): Promise<any> {
  return new Promise((resolve, reject) => {
    const userToken = auth?.userLoggedIn?.userToken || auth?.user?.token

    // Add required date fields to FormData if not already present
    if (!body.has('issueDate')) {
      body.append('issueDate', new Date().toISOString().split('T')[0]) // Current date as default
    }
    if (!body.has('expirationDate')) {
      // Default expiration 10 years from now
      const expirationDate = new Date()
      expirationDate.setFullYear(expirationDate.getFullYear() + 10)
      body.append('expirationDate', expirationDate.toISOString().split('T')[0])
    }

    axios
      .post(
        `${uploadService.URL}/public/document/user/${params.userid}/file/${params.pathfile}`,
        body,
        {
          headers: {
            Authorization: `${userToken}`,
            // Note: Don't set Content-Type manually for FormData, let axios handle it
          },
        },
      )
      .then(function (response) {
        resolve(response.data)
      })
      .catch(function (error) {
        // Enhanced error handling
        if (error.response) {
          console.error('Upload error:', error.response.data)
          reject({
            status: error.response.status,
            message: error.response.data?.message || 'Upload failed',
            data: error.response.data,
          })
        } else {
          console.error('Network error:', error.message)
          reject(error)
        }
      })
  })
}

export async function genUserImageUrl(auth: IAuthContext, body: IGetUserImage): Promise<any> {
  return new Promise((resolve, reject) => {
    const userToken = auth?.userLoggedIn?.userToken
    axios
      .post(
        `${restfulService.URL}/cms/usermanage/genimageurl-checkin`,
        { ...body },
        {
          headers: {
            authorization: userToken,
          },
        },
      )
      .then(function (response) {
        resolve(response.data)
      })
      .catch(function (error) {
        // console.log('error = ', error.response.data.message)
        reject(error)
      })
  })
}

export const getOmiselinkCheckin = async (
  posData: IApiRequestGetOmiselink,
  token: string,
): Promise<IOmiselinkResponse> => {
  const response = await axios.post(`${restfulService.URL}/customer-api/payment-checkin`, posData, {
    headers: {
      'Content-Type': 'application/json',
      // Add Authorization header if needed, e.g.:
      Authorization: `${token}`,
    },
  })

  return response.data
}

// Simple Process Omise token service
export const processOmiseTokenWithPayment = async (
  omiseToken: string,
  token: string,
): Promise<ProcessOmiseTokenResponse> => {
  try {
    const response = await axios.post(
      `${restfulService.URL}/customer-api/process-omise-token`,
      { omise_token: omiseToken },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      },
    )
    return response.data
  } catch (error) {
    return {
      result: false,
      message: 'Something went wrong',
    }
  }
}
